import { FC, ReactNode, useRef, useState } from 'react';
import { ArrowRight } from 'phosphor-react';
import { NODE_UL, render } from 'storyblok-rich-text-react-renderer';
import { Footer, ShopContent } from '../../../types/cms_shared_types';
import { Button } from '../../Common/Button';
import { HelpButton } from './HelpButton';
import { Conditional } from '../../Common/Conditional';
import { LoadingSpinner } from '../../Common/LoadingSpinner';

interface DefaultBottomBarProps {
  text?: string;
  continueButtonDisabled: boolean;
  onClick: () => void;
  blackButton?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  cmsShopContent: ShopContent;
  footerContent?: Footer;
  loading?: boolean;
}

export const DefaultBottomBar: FC<DefaultBottomBarProps> = ({
  text,
  continueButtonDisabled,
  onClick,
  blackButton,
  iconLeft,
  iconRight = <ArrowRight size={18} />,
  cmsShopContent,
  footerContent,
  loading,
}) => {
  const scrollTo = useRef<HTMLInputElement>(null);
  const [showFooter, setShowFooter] = useState(false);

  const executeScroll = () => scrollTo?.current?.scrollIntoView({ behavior: 'smooth', inline: 'start' });

  const displayMedia = () => {
    return footerContent?.mediaUrl ? (
      <>
        <Conditional
          condition={footerContent?.mediaUrl.endsWith('.mp4') ?? false}
          trueRender={
            <video id={'footerVideo'} autoPlay playsInline loop className="w-full">
              <source src={footerContent?.mediaUrl} type="video/mp4" />
            </video>
          }
          falseRender={<img src={footerContent?.mediaUrl}></img>}
        />
        <div className="mt-4 mb-10">{render(footerContent?.mediaFooter)}</div>
      </>
    ) : null;
  };

  return (
    <div className={`${footerContent?.displayFooter ? 'sticky' : ''} fixed bottom-0 inset-x-0`}>
      <div className="flex flex-row items-center h-20 p-6 border border-input-gray bg-input-gray">
        <div className="w-1/3 mr-auto sm:w-1/5" />

        <div className="flex flex-col items-center pr-2 mx-auto grow">
          <Button
            filled
            size="large"
            black={blackButton}
            text={text ?? cmsShopContent.continueButton}
            leftIcon={iconLeft}
            rightIcon={loading ? <LoadingSpinner size="small" /> : iconRight}
            disabled={continueButtonDisabled || loading}
            onClick={onClick}
          />
        </div>

        <div className="flex justify-end w-1/3 ml-auto sm:w-1/5">
          <Conditional
            condition={Boolean(footerContent?.displayFooter)}
            trueRender={
              <HelpButton
                text={cmsShopContent.helpButton}
                onClick={() => {
                  setShowFooter(true);
                  // Unfortunately we have (?) to use this hack, otherwise 'showFooter' is
                  // not yet set to true (it takes time when using 'useState').
                  // So if we run 'executeScroll' immediately after, no scrollRef will be found
                  // and therefore no scrolling will happen.
                  // 100 ms seems to be enough for the change to register, while also being good UX.
                  setTimeout(() => executeScroll(), 100);
                }}
              />
            }
          />
        </div>
      </div>

      <Conditional
        condition={showFooter}
        trueRender={
          <div
            ref={scrollTo}
            className={`${
              footerContent?.displayFooter ? 'sm:flex' : ''
            } justify-evenly  sm:p-6 bg-sami-dark text-input-gray absolute`}
          >
            <div className="block my-10 sm:hidden mx-mobile-side">{displayMedia()}</div>

            <div className="h-auto my-10 sm:w-5/12 sm:mx-10 mx-mobile-side sm:my-20">
              {render(footerContent?.leftColumn)}
            </div>
            <div className="h-auto my-10 sm:w-5/12 sm:mx-10 mx-mobile-side sm:my-20">
              <div className="hidden sm:block">{displayMedia()}</div>

              {render(footerContent?.rightColumn, {
                nodeResolvers: {
                  [NODE_UL]: (children) => <ul className="list-disc">{children}</ul>,
                },
              })}
            </div>
          </div>
        }
      />
    </div>
  );
};
